import React from 'react';
import { Button } from 'react-bootstrap';

const PropertyPanel = ({ element, onUpdateProperty, onDelete, onDuplicate }) => {
    if (!element) {
        return (
            <div className="property-panel">
                <div className="no-selection">
                    <div>✋</div>
                    <p>Kein Element ausgewählt</p>
                    <small className="text-muted">
                        Klicke auf ein Element auf dem Canvas, um seine Eigenschaften zu bearbeiten.
                    </small>
                </div>
            </div>
        );
    }

    const handleChange = (field, value) => {
        onUpdateProperty({
            ...element,
            [field]: value
        });
    };

    const handleNumberChange = (field, value) => {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            handleChange(field, numValue);
        }
    };

    return (
        <div className="property-panel">
            <h6 className="mb-3">Eigenschaften</h6>

            {/* Element Type */}
            <div className="property-group">
                <h6>Element</h6>
                <div className="text-muted">
                    <small>{getElementTypeLabel(element.element_type)}</small>
                </div>
            </div>

            {/* Position & Size */}
            <div className="property-group">
                <h6>Position & Größe</h6>
                <div className="row g-2">
                    <div className="col-6">
                        <div className="property-field">
                            <label>X (mm)</label>
                            <input
                                type="number"
                                value={element.x_position}
                                onChange={(e) => handleNumberChange('x_position', e.target.value)}
                                step="1"
                            />
                        </div>
                    </div>
                    <div className="col-6">
                        <div className="property-field">
                            <label>Y (mm)</label>
                            <input
                                type="number"
                                value={element.y_position}
                                onChange={(e) => handleNumberChange('y_position', e.target.value)}
                                step="1"
                            />
                        </div>
                    </div>
                    <div className="col-6">
                        <div className="property-field">
                            <label>Breite (mm)</label>
                            <input
                                type="number"
                                value={element.width}
                                onChange={(e) => handleNumberChange('width', e.target.value)}
                                step="1"
                                min="1"
                            />
                        </div>
                    </div>
                    <div className="col-6">
                        <div className="property-field">
                            <label>Höhe (mm)</label>
                            <input
                                type="number"
                                value={element.height}
                                onChange={(e) => handleNumberChange('height', e.target.value)}
                                step="1"
                                min="1"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Text Content */}
            {(element.element_type === 'text' || element.element_type === 'address') && (
                <div className="property-group">
                    <h6>Text</h6>
                    <div className="property-field">
                        <label>Inhalt</label>
                        <textarea
                            value={element.text_content || ''}
                            onChange={(e) => handleChange('text_content', e.target.value)}
                            rows={4}
                        />
                        {element.element_type === 'address' && (
                            <small className="text-muted d-block mt-1">
                                Variablen: {'{{photographer.name}}'}, {'{{photographer.address}}'}
                            </small>
                        )}
                    </div>
                </div>
            )}

            {/* Font Properties */}
            {(element.element_type === 'text' || element.element_type === 'address') && (
                <div className="property-group">
                    <h6>Schrift</h6>
                    <div className="property-field">
                        <label>Schriftart</label>
                        <select
                            value={element.font_family || 'Arial'}
                            onChange={(e) => handleChange('font_family', e.target.value)}
                        >
                            <option value="Arial">Arial</option>
                            <option value="Times New Roman">Times New Roman</option>
                            <option value="Courier New">Courier New</option>
                            <option value="Georgia">Georgia</option>
                            <option value="Verdana">Verdana</option>
                        </select>
                    </div>
                    <div className="row g-2">
                        <div className="col-6">
                            <div className="property-field">
                                <label>Größe (px)</label>
                                <input
                                    type="number"
                                    value={element.font_size || 12}
                                    onChange={(e) => handleNumberChange('font_size', e.target.value)}
                                    min="6"
                                    max="72"
                                />
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="property-field">
                                <label>Gewicht</label>
                                <select
                                    value={element.font_weight || 'normal'}
                                    onChange={(e) => handleChange('font_weight', e.target.value)}
                                >
                                    <option value="normal">Normal</option>
                                    <option value="bold">Fett</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="property-field">
                        <label>Ausrichtung</label>
                        <select
                            value={element.text_align || 'left'}
                            onChange={(e) => handleChange('text_align', e.target.value)}
                        >
                            <option value="left">Links</option>
                            <option value="center">Zentriert</option>
                            <option value="right">Rechts</option>
                        </select>
                    </div>
                    <div className="property-field">
                        <label>Textfarbe</label>
                        <input
                            type="color"
                            value={element.font_color || '#000000'}
                            onChange={(e) => handleChange('font_color', e.target.value)}
                        />
                    </div>
                </div>
            )}

            {/* Background & Border */}
            {(element.element_type === 'text' || element.element_type === 'address' || element.element_type === 'rectangle') && (
                <div className="property-group">
                    <h6>Hintergrund & Rahmen</h6>
                    <div className="property-field">
                        <label>Hintergrundfarbe</label>
                        <div className="d-flex gap-2 align-items-center">
                            <input
                                type="color"
                                value={element.background_color || '#ffffff'}
                                onChange={(e) => handleChange('background_color', e.target.value)}
                            />
                            <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => handleChange('background_color', null)}
                            >
                                Transparent
                            </Button>
                        </div>
                    </div>
                    <div className="property-field">
                        <label>Rahmenfarbe</label>
                        <input
                            type="color"
                            value={element.border_color || '#000000'}
                            onChange={(e) => handleChange('border_color', e.target.value)}
                        />
                    </div>
                    <div className="property-field">
                        <label>Rahmenstärke (px)</label>
                        <input
                            type="number"
                            value={element.border_width || 0}
                            onChange={(e) => handleNumberChange('border_width', e.target.value)}
                            min="0"
                            max="10"
                        />
                    </div>
                </div>
            )}

            {/* Line Properties */}
            {element.element_type === 'line' && (
                <div className="property-group">
                    <h6>Linie</h6>
                    <div className="property-field">
                        <label>Farbe</label>
                        <input
                            type="color"
                            value={element.border_color || element.font_color || '#000000'}
                            onChange={(e) => handleChange('border_color', e.target.value)}
                        />
                    </div>
                    <div className="property-field">
                        <label>Stärke (px)</label>
                        <input
                            type="number"
                            value={element.border_width || 1}
                            onChange={(e) => handleNumberChange('border_width', e.target.value)}
                            min="1"
                            max="20"
                        />
                    </div>
                </div>
            )}

            {/* Advanced */}
            <div className="property-group">
                <h6>Erweitert</h6>
                <div className="property-field">
                    <label>Ebene (Z-Index)</label>
                    <input
                        type="number"
                        value={element.z_index || 0}
                        onChange={(e) => handleNumberChange('z_index', e.target.value)}
                        min="0"
                    />
                </div>
                <div className="property-field">
                    <label>Deckkraft</label>
                    <input
                        type="range"
                        value={element.opacity || 1.0}
                        onChange={(e) => handleNumberChange('opacity', e.target.value)}
                        min="0"
                        max="1"
                        step="0.1"
                    />
                    <small className="text-muted">{Math.round((element.opacity || 1.0) * 100)}%</small>
                </div>
                <div className="property-field">
                    <label>Rotation (Grad)</label>
                    <input
                        type="number"
                        value={element.rotation || 0}
                        onChange={(e) => handleNumberChange('rotation', e.target.value)}
                        min="-180"
                        max="180"
                    />
                </div>
                <div className="property-field">
                    <label>Seite</label>
                    <select
                        value={element.page_type || 'all'}
                        onChange={(e) => handleChange('page_type', e.target.value)}
                    >
                        <option value="all">Alle Seiten</option>
                        <option value="first">Nur 1. Seite</option>
                        <option value="following">Nur Folgeseiten</option>
                    </select>
                </div>
            </div>

            {/* Actions */}
            <div className="d-grid gap-2">
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => onDuplicate(element)}
                >
                    Duplizieren
                </Button>
                <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => onDelete(element.element_id)}
                >
                    Löschen
                </Button>
            </div>
        </div>
    );
};

const getElementTypeLabel = (type) => {
    const labels = {
        'text': 'Text',
        'address': 'Adresse',
        'logo': 'Logo',
        'image': 'Bild',
        'rectangle': 'Rechteck',
        'line': 'Linie'
    };
    return labels[type] || type;
};

export default PropertyPanel;
